import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ImagePlus, BookOpen, Loader2 } from "lucide-react";
import { compressImage, slugify } from "./imageUtils";

// Photos are uploaded to the server immediately; reading starts there in the background.
export const PlantUpload = ({ plant, sessionId, pageCount, onUploaded }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(null);
  const slug = slugify(plant);

  const upload = async (list) => {
    const files = Array.from(list).filter((f) => f.type.startsWith("image/") || /\.(jpe?g|png|webp|heic)$/i.test(f.name));
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setBusy(`Uploading ${i + 1} of ${files.length}…`);
      try {
        const image_base64 = await compressImage(files[i]);
        await api.post(`/verification/sessions/${sessionId}/pages`, { plant, name: files[i].name, image_base64 });
        ok++;
        onUploaded();
      } catch (e) { toast.error(`${files[i].name}: ${formatApiErrorDetail(e.response?.data?.detail) || e.message}`); }
    }
    setBusy(null);
    if (ok) toast.success(`${ok} page(s) saved. Reading continues even if you close the website.`);
  };

  return (
    <div data-testid={`upload-zone-${slug}`} onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); if (!busy) upload(e.dataTransfer.files); }}
      className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-chivo font-black text-lg text-slate-900"><BookOpen className="h-5 w-5 text-sky-600" /> {plant} Notebook</div>
        <span data-testid={`upload-count-${slug}`} className="text-xs text-slate-500">{pageCount} page(s) saved</span>
      </div>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" data-testid={`upload-input-${slug}`}
        onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
      <Button type="button" variant="outline" disabled={!!busy} onClick={() => ref.current?.click()} data-testid={`upload-button-${slug}`} className="w-full h-16 text-base">
        {busy ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />{busy}</> : <><ImagePlus className="h-5 w-5 mr-2" />Add {plant} page photos</>}
      </Button>
    </div>
  );
};
